import clsx from 'clsx';
import { useRef } from 'react';
import { useTransition, animated } from 'react-spring';
import { useForceRender } from '../../../hooks/useForceRender.js';
import { useUpdate } from '../../../hooks/useUpdate.js';
import { useNotifications } from '../hooks/useNotifications.js';
import { NotificationWrapper } from '../NotificationWrapper.js';
import styles from '../styles/Notifications.module.scss.js';
import { useNotificationsEvents } from '../utils/events.js';
import { NotificationsContext } from './useNotificationsContext.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Portal } from '../../portal/Portal.js';

const POSITIONS = ['top-left', 'top-right', 'top-center', 'bottom-left', 'bottom-right', 'bottom-center'];
const defaultPosition = 'top-right';
const NotificationsProvider = ({
  className,
  position = defaultPosition,
  autoClose = 5000,
  transitionDuration = 250,
  limit = 5,
  zIndex = 900,
  from = 50,
  to = 0,
  style,
  children,
  NotificationComponent
}) => {
  const forceRender = useForceRender();
  const refs = useRef({});
  const previousLength = useRef(0);
  const {
    notifications,
    queue,
    showNotification,
    updateNotification,
    hideNotification,
    clean,
    cleanQueue
  } = useNotifications({
    limit
  });
  const positioning = (POSITIONS.includes(position) ? position : defaultPosition).split('-');
  const fromAxis = positioning[1] === 'left' || positioning[1] === 'right' ? {
    x: from
  } : {
    y: from
  };
  const toAxis = positioning[1] === 'left' || positioning[1] === 'right' ? {
    x: to
  } : {
    y: to
  };
  const endAxis = positioning[1] === 'left' || positioning[1] === 'right' ? {
    x: from
  } : {
    y: from
  };
  const transitions = useTransition(notifications, {
    keys: notification => notification.id,
    delay: 200,
    from: {
      opacity: 0,
      ...fromAxis,
      duration: transitionDuration
    },
    enter: {
      opacity: 1,
      ...toAxis,
      duration: transitionDuration
    },
    leave: {
      opacity: 0,
      ...endAxis
    }
  });
  const ctx = {
    notifications,
    queue,
    showNotification,
    hideNotification,
    updateNotification,
    clean,
    cleanQueue
  };
  useUpdate(() => {
    if (notifications.length > previousLength.current) {
      setTimeout(() => forceRender(), 0);
    }
    previousLength.current = notifications.length;
  }, [notifications]);
  useNotificationsEvents(ctx);
  const items = transitions((rest, element) => {
    return /*#__PURE__*/jsx(animated.div, {
      className: clsx(styles.item),
      ref: {
        current: refs.current[element.id]
      },
      style: {
        zIndex,
        ...rest,
        ...style
      },
      children: /*#__PURE__*/jsx(NotificationWrapper, {
        innerRef: node => {
          refs.current[element.id] = node;
        },
        notification: element,
        onHide: hideNotification,
        autoClose: autoClose,
        NotificationComponent: NotificationComponent
      })
    }, element.id);
  });
  return /*#__PURE__*/jsxs(NotificationsContext.Provider, {
    value: ctx,
    children: [/*#__PURE__*/jsx(Portal, {
      children: !!notifications.length && /*#__PURE__*/jsx("div", {
        className: clsx(styles.root, {
          [styles.top_left]: positioning[0] === 'top' && positioning[1] === 'left',
          [styles.top_right]: positioning[0] === 'top' && positioning[1] === 'right',
          [styles.top_center]: positioning[0] === 'top' && positioning[1] === 'center',
          [styles.bottom_left]: positioning[0] === 'bottom' && positioning[1] === 'left',
          [styles.bottom_right]: positioning[0] === 'bottom' && positioning[1] === 'right',
          [styles.bottom_center]: positioning[0] === 'bottom' && positioning[1] === 'center'
        }, className),
        children: items
      })
    }), children]
  });
};

export { NotificationsProvider };
